import { ApiError } from '@dataroom/shared';

/**
 * The platform KEYS console and the unlock page (ADR-0069 slice B) as the
 * admin app sees them: the status the API reports (key ids only, never a key),
 * plain words for every code, and the kit download — built in memory, never
 * stored by the app. The readers are business owners, possibly under stress:
 * no jargon (escrow, wrap, root, MAC, purpose, key ids) reaches the page.
 */

export type KeyPurpose = 'document' | 'totp' | 'dd_field' | 'mac';

export const PURPOSES: KeyPurpose[] = ['document', 'totp', 'dd_field', 'mac'];

export const PURPOSE_LABELS: Record<KeyPurpose, string> = {
  document: 'Documents',
  totp: 'Authenticator secrets',
  dd_field: 'Bank account numbers',
  mac: 'Email codes',
};

/** `mac` is never retired: unsubscribe links already sent verify under every version. */
export const RETIRABLE: ReadonlySet<KeyPurpose> = new Set(['document', 'totp', 'dd_field']);

export interface KeyVersionView {
  purpose: KeyPurpose;
  version: number;
  keyId: string;
  status: 'active' | 'retired';
  createdAt: string;
  /** Set while the version is inside its settle window (it takes writes after this). */
  settlesAt: string | null;
  /** The version new data is written under. */
  writing: boolean;
}

export type RecoveryReason =
  | 'api_root_absent'
  | 'no_verified_kit'
  | 'escrow_incomplete'
  | 'too_few_platform_owners';

export const RECOVERY_REASON_LABELS: Record<RecoveryReason, string> = {
  api_root_absent: 'Key management isn’t switched on for this site yet.',
  no_verified_kit: 'No recovery kit has been created and Verified.',
  escrow_incomplete: 'Some keys are not in the current kit yet. Create a new kit and Verify it.',
  too_few_platform_owners:
    'Recovery needs at least two platform owners, so the kit never depends on one person. ' +
    'Ask engineering to add a second platform owner.',
};

/**
 * While a current kit exists every real key is in it, so a key it does not
 * cover was added by someone the app does not trust (ADR-0069 W16). A new kit
 * would cover that key too, so the owner is told NOT to make one.
 */
export const ESCROW_INCOMPLETE_WITH_CURRENT_KIT =
  'A key appeared that the recovery kit does not cover. Don’t create a new kit. Call engineering.';

/** The plain-words line for one reason, given whether a current kit exists. */
export function recoveryReasonLabel(
  reason: RecoveryReason,
  recovery: Pick<RecoveryView, 'currentId'>,
): string {
  if (reason === 'escrow_incomplete' && recovery.currentId) {
    return ESCROW_INCOMPLETE_WITH_CURRENT_KIT;
  }
  return RECOVERY_REASON_LABELS[reason] ?? reason;
}

/** The site a kit belongs to, in plain words (kits only work on the site that made them). */
export function tierLabel(tier: string): string {
  if (tier === 'prod') return 'Live site';
  if (tier === 'dev') return 'Test site (DEV)';
  return `Local test site (${tier})`;
}

export interface RecoveryView {
  armed: boolean;
  currentId: string | null;
  /** When the current kit was created (shown instead of its id). */
  currentCreatedAt?: string | null;
  pendingIds: string[];
  /** When each pending kit was created, oldest first. */
  pendingCreatedAt?: string[];
  verifiedAt: string | null;
  verifyDueAt: string | null;
  verifyOverdue: boolean;
  unescrowed: string[];
  reasons: RecoveryReason[];
}

export interface KeyEventView {
  id: string;
  at: string;
  event: string;
  outcome: 'ok' | 'refused' | 'failed';
  actorEmail: string | null;
  recoveryKeyId: string | null;
  purpose: KeyPurpose | null;
  version: number | null;
}

export type KeysStatus =
  | { managed: false }
  | {
      managed: true;
      tier: string;
      platformOwners: number;
      versions: KeyVersionView[];
      recovery: RecoveryView;
      retiring: { purpose: KeyPurpose; version: number }[];
      events: KeyEventView[];
    };

/** What POST /platform/keys/kit returns — held only in the page's memory. */
export interface CreatedKit {
  kit: Record<string, unknown>;
  fileName: string;
  recoveryCode: string;
  recoveryKeyId: string;
}

export const EVENT_LABELS: Record<string, string> = {
  kit_email_code_sent: 'Kit code emailed',
  kit_created: 'Kit created',
  kit_verified: 'Kit Verified',
  kit_verify_failed: 'Kit Verify failed',
  unlock_succeeded: 'Unlocked',
  unlock_failed: 'Unlock failed',
  rotated: 'Key rotated',
  rotate_refused: 'Rotation refused',
  retire_started: 'Retirement started',
  retire_refused: 'Retirement refused',
  retired: 'Version retired',
  retire_failed: 'Retirement failed',
  recovery_check: 'Daily recovery check',
  unwrapped_version: 'Unknown key found (not used)',
  unescrowed_version: 'Key not in the recovery kit (not used)',
};

export const PICK_KIT_FILE_MESSAGE =
  'Pick the recovery kit file. It is the .json file named closingvault-recovery-kit-…, saved in ' +
  'the company password manager.';
export const NOT_A_KIT_MESSAGE =
  'This file is not a ClosingVault recovery kit. Pick the closingvault-recovery-kit-….json file.';

/**
 * Plain words for every error code the keys console and the unlock page can
 * get back. Anything unmapped keeps the API's own (already user-facing) text.
 */
export const KEYS_ERROR_MESSAGES: Record<string, string> = {
  version_not_escrowed:
    'A key appeared that the recovery kit does not cover, so no new kit was made. Don’t create ' +
    'a new kit. Call engineering.',
  escrow_missing:
    'This kit doesn’t cover the newest keys. Use the newest kit, or create a new one.',
  unseal_failed:
    'This kit couldn’t open one of the keys. Create a new kit and contact engineering.',
  settling: 'The new key is still starting up. Try again in a few minutes.',
  unarmed: 'Create and Verify a recovery kit first.',
  version_unopenable:
    'A key can’t be opened right now. Contact engineering before changing anything.',
  not_unlockable:
    'The recovery kit can’t fix this problem. Nothing was changed. Call engineering now, and ' +
    'don’t try to reset or create anything yourself.',
  retire_in_flight: 'An older key is already being retired. Try again once that finishes.',
  active_version: 'This key is the one new data uses. Make a new key first, then retire this one.',
  already_retired: 'This key is already retired.',
  not_retirable: 'Email codes are never retired: links already emailed must keep working.',
  not_found: 'That key doesn’t exist any more. Refresh the page.',
  no_roots: 'Key management isn’t switched on for this site yet. There is nothing to do here.',
  keys_not_managed:
    'Key management isn’t switched on for this site yet. There is nothing to do here.',
  kit_malformed: NOT_A_KIT_MESSAGE,
  kit_wrong_env: 'This kit belongs to the other site. Kits only work on the site that made them.',
  kit_not_accepted:
    'This isn’t the company’s current recovery kit. Use the kit created most recently.',
  kit_wrong_code:
    'The kit file and the recovery code don’t match. Check the code in the password manager ' +
    'and type it again.',
  kit_kit_mismatch: 'This kit file is damaged. Use the copy in the company password manager.',
  invalid_email_code: 'That emailed code isn’t right or has expired. Send a new code.',
  step_up_invalid:
    'That authenticator code isn’t right. Enter the code your authenticator app shows now.',
  unlock_in_progress: 'An unlock is already running. Wait a moment, then look again.',
};

/** The same error with plain words for its code (keeps status, problem and wait hint). */
export function plainKeysError(error: unknown): unknown {
  if (!(error instanceof ApiError)) return error;
  const code = error.problem?.code;
  const message = code ? KEYS_ERROR_MESSAGES[code] : undefined;
  if (!message) return error;
  return new ApiError(error.status, message, error.problem, error.debug, error.retryAfterSeconds);
}

/** Runs an API call and rethrows any failure with plain words for its code. */
export async function keysCall<T>(call: () => Promise<T>): Promise<T> {
  try {
    return await call();
  } catch (err) {
    throw plainKeysError(err);
  }
}

export const KITS_QUERY_KEY = ['platform', 'keys'];

/** How often the keys console re-reads itself while a retirement runs. */
export const RETIRE_POLL_MS = 15_000;
/** How often the unlock page checks whether the unlocked service is up. */
export const UNLOCK_POLL_MS = 5_000;

export const KIT_CODE_PLACEHOLDER = 'XXXX-XXXX-XXXX-…';
export const KIT_CODE_HINT = 'Dashes, spaces and capital letters don’t matter.';

/** The kit as a file, made in memory: a Blob URL for one download, revoked after. */
export function downloadKit(created: Pick<CreatedKit, 'kit' | 'fileName'>): void {
  const blob = new Blob([JSON.stringify(created.kit, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = created.fileName;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Revoked on the next tick, once the browser has started the download.
    setTimeout(() => URL.revokeObjectURL(url), 0);
  }
}

/**
 * Reads an uploaded kit file as JSON (never sent anywhere but the API). Its
 * failures are ApiErrors (status 400, no request made) so the page shows these
 * exact words rather than a generic "something went wrong".
 */
export async function readKitFile(file: File | null): Promise<unknown> {
  if (!file) throw new ApiError(400, PICK_KIT_FILE_MESSAGE);
  if (file.size > 64 * 1024) throw new ApiError(400, NOT_A_KIT_MESSAGE);
  const text = await file.text();
  try {
    return JSON.parse(text) as unknown;
  } catch {
    throw new ApiError(400, NOT_A_KIT_MESSAGE);
  }
}

export function formatWhen(iso: string | null): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
}

/** A date alone, e.g. "2 Nov 2026". */
export function formatDay(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}
